import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';

class AnalyticsService {
  private appInsights: ApplicationInsights | null = null;
  private reactPlugin: ReactPlugin | null = null;
  private isInitialized = false;

  initialize() {
    const instrumentationKey = import.meta.env.VITE_APPINSIGHTS_INSTRUMENTATION_KEY;
    
    if (!instrumentationKey || instrumentationKey === 'your_instrumentation_key_here') {
      console.warn('Application Insights instrumentation key not configured');
      return;
    }

    try {
      this.reactPlugin = new ReactPlugin();
      this.appInsights = new ApplicationInsights({
        config: {
          instrumentationKey: instrumentationKey,
          extensions: [this.reactPlugin],
          enableAutoRouteTracking: true,
          disableFetchTracking: false,
          enableCorsCorrelation: false, // Disabled to prevent CORS issues with external APIs
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
          autoTrackPageVisitTime: true,
          disableAjaxTracking: false,
          disableExceptionTracking: false,
          maxBatchSizeInBytes: 10000,
          maxBatchInterval: 15000
        }
      });
      
      this.appInsights.loadAppInsights();
      this.isInitialized = true;
      console.log('Application Insights initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Application Insights:', error);
    }
  }

  // Track when a user starts the form
  trackFormStarted(customerId: string, sessionId: string) {
    if (!this.appInsights || !this.isInitialized) return;
    
    this.appInsights.trackEvent({
      name: 'FormStarted',
      properties: {
        customerId,
        sessionId,
        timestamp: new Date().toISOString()
      }
    });
  }

  // Track step completion
  trackStepCompleted(stepName: string, stepNumber: number, timeSpent: number, sessionId: string, customerId: string) {
    if (!this.appInsights || !this.isInitialized) return;
    
    this.appInsights.trackEvent({
      name: 'StepCompleted',
      properties: {
        stepName,
        stepNumber,
        sessionId,
        customerId,
        timestamp: new Date().toISOString()
      },
      measurements: {
        timeSpentSeconds: timeSpent / 1000
      }
    });
  }

  // Track field changes (debounced)
  trackFieldChange(fieldName: string, hasValue: boolean, stepName: string, sessionId: string) {
    if (!this.appInsights || !this.isInitialized) return;
    
    this.appInsights.trackEvent({
      name: 'FieldChanged',
      properties: {
        fieldName,
        hasValue: hasValue.toString(),
        stepName,
        sessionId,
        timestamp: new Date().toISOString()
      }
    });
  }

  // Track form abandonment
  trackFormAbandoned(lastStep: string, stepNumber: number, timeSpent: number, sessionId: string, customerId: string) {
    if (!this.appInsights || !this.isInitialized) return;
    
    this.appInsights.trackEvent({
      name: 'FormAbandoned',
      properties: {
        lastStep,
        stepNumber,
        sessionId,
        customerId,
        timestamp: new Date().toISOString()
      },
      measurements: {
        totalTimeSpentSeconds: timeSpent / 1000,
        completionPercentage: (stepNumber / 7) * 100
      }
    });
  }

  // Track successful form submission
  trackFormSubmitted(sessionId: string, customerId: string, totalTime: number) {
    if (!this.appInsights || !this.isInitialized) return;
    
    this.appInsights.trackEvent({
      name: 'FormSubmitted',
      properties: {
        sessionId,
        customerId,
        timestamp: new Date().toISOString()
      },
      measurements: {
        totalTimeMinutes: totalTime / 60000
      }
    });
  }

  // Track validation errors
  trackValidationError(fieldName: string, errorType: string, stepName: string, sessionId: string) {
    if (!this.appInsights || !this.isInitialized) return;
    
    this.appInsights.trackEvent({
      name: 'ValidationError',
      properties: {
        fieldName,
        errorType,
        stepName,
        sessionId,
        timestamp: new Date().toISOString()
      }
    });
  }

  // Track user navigation (back button usage)
  trackNavigation(action: 'next' | 'back', fromStep: string, toStep: string, sessionId: string) {
    if (!this.appInsights || !this.isInitialized) return;
    
    this.appInsights.trackEvent({
      name: 'Navigation',
      properties: {
        action,
        fromStep,
        toStep,
        sessionId,
        timestamp: new Date().toISOString()
      }
    });
  }

  // Track page views
  trackPageView(pageName: string, customerId?: string) {
    if (!this.appInsights || !this.isInitialized) return;
    
    this.appInsights.trackPageView({
      name: pageName,
      properties: {
        customerId: customerId || 'unknown',
        timestamp: new Date().toISOString()
      }
    });
  }

  // Track exceptions
  trackException(error: Error, severityLevel?: number) {
    if (!this.appInsights || !this.isInitialized) return;
    
    this.appInsights.trackException({
      exception: error,
      severityLevel: severityLevel || 3
    });
  }

  // Custom metric tracking
  trackMetric(name: string, value: number, properties?: { [key: string]: string }) {
    if (!this.appInsights || !this.isInitialized) return;
    
    this.appInsights.trackMetric({
      name,
      average: value,
      properties
    });
  }

  // Get the React plugin for component tracking
  getReactPlugin() {
    return this.reactPlugin;
  }

  // Flush any pending telemetry
  flush() {
    if (this.appInsights && this.isInitialized) {
      this.appInsights.flush();
    }
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();